import { Grid, Typography } from '@mui/material';
import { FC } from 'react';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { v4 as uuidv4 } from 'uuid';
import Router from 'next/router';
import { Field } from '@models/CriteriaData';
import {
  capitalizeFirstLetter,
  getTextInitials,
  removeHtmlTags,
  rewriteRouteWithNewCriteria,
} from '../../../shared/functions';

interface ChipContainerProps {
  title: string;
  chipTexts: string[];
  queryName?: Field;
  routeAddress?: string;
  areKeywordsMatched?: boolean;
  hoverCallback?: (enter: boolean, word: string) => void;
  theme?: 'black' | 'white';
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chip: {
      maxWidth: '100%',
      borderColor: '#d0d0d0',
      '&&:hover': {
        backgroundColor: theme.palette.text.secondary,
        color: 'white',
      },
    },
    avatar: {
      backgroundColor: theme.palette.text.secondary,
    },
  }),
);

export const ChipContainer: FC<ChipContainerProps> = ({
  title,
  chipTexts,
  queryName,
  routeAddress,
  hoverCallback,
  theme = 'black',
}) => {
  const classes = useStyles();

  const routeTo = (event, filter: string) => {
    event.preventDefault();
    event.stopPropagation();

    if (routeAddress) {
      rewriteRouteWithNewCriteria(
        Router,
        { q: filter, field: queryName, accuracy: 'Vague' },
        routeAddress,
      );
    }
  };

  const WordChip = (props) => (
    <Chip
      className={classes.chip}
      style={{ borderRadius: props.squared ? 3 : null }}
      variant={'outlined'}
      avatar={
        <Avatar className={classes.avatar}>
          <Typography style={{ color: 'white' }} variant={'caption'}>
            {getTextInitials(props.word)}
          </Typography>
        </Avatar>
      }
      onMouseEnter={() => {
        if (hoverCallback) {
          hoverCallback(true, props.word?.replace(/<\/?[^>]+(>|$)/g, ''));
        }
      }}
      onMouseLeave={() => {
        if (hoverCallback) {
          hoverCallback(false, props.word?.replace(/<\/?[^>]+(>|$)/g, ''));
        }
      }}
      onClick={(event) => {
        event.stopPropagation();
        // eslint-disable-next-line no-unused-expressions
        props.route &&
          routeTo(event, capitalizeFirstLetter(removeHtmlTags(props.word)));
      }}
      label={
        <Typography
          noWrap
          sx={{
            color: theme === 'black' ? '#d2d1d1' : '#e5e7eb',
            '&:hover': {
              color: 'white',
            },
          }}
          variant={'body2'}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: capitalizeFirstLetter(props.word, true),
            }}
          />
        </Typography>
      }
    />
  );

  return (
    <Grid container xs={12} spacing={1} sx={{ pb: 1 }}>
      {chipTexts?.length > 0 && (
        <Grid container item xs={12}>
          <b>{title}</b>
        </Grid>
      )}
      {chipTexts.slice(0, 5).map((text) => (
        <Grid item xs={12} sm={6} key={text + uuidv4()}>
          <WordChip word={text} route={Boolean(queryName)} />
        </Grid>
      ))}
    </Grid>
  );
};
